import { useState } from 'react';

import utils from '../libs/utils';

interface LoadingMap {
  [reference: string]: boolean
}

interface ErrorMessageMap {
  [reference: string]: string
}

interface Status {
  isLoading: boolean
  errorMessage: string
}

const useLoadingStatusMap = () => {
  // to ensure all values retrieved are latest/updated, a reference type variable map is required (centralized value reference)
  const [referenceMap] = useState<Map<string,Status>>(new Map<string,Status>());

  const [isLoading, _setIsLoading] = useState<LoadingMap>({});
  const [errorMessage, _setErrorMessage] = useState<ErrorMessageMap>({});

  const _syncReferenceMap = () => {
    const newLoadingMap: LoadingMap = {};
    const newErrorMessageMap: ErrorMessageMap = {};

    for (const [ref, status] of Array.from(referenceMap.entries())) {
      newLoadingMap[ref] = status.isLoading;
      newErrorMessageMap[ref] = status.errorMessage;
    }

    _setIsLoading(newLoadingMap);
    _setErrorMessage(newErrorMessageMap);
  };

  const _setReferenceStatus = (ref: string, status: Partial<Status>) => {
    const currentStatus = referenceMap.get(ref);
    const {
      isLoading = currentStatus?.isLoading || false,
      errorMessage = currentStatus?.errorMessage || ''
    } = status;
    referenceMap.set(ref, { isLoading, errorMessage });
    _syncReferenceMap();
  };

  const setIsLoading = (ref: string, isLoading: boolean) => {
    _setReferenceStatus(ref, { isLoading });
  };

  const setErrorMessage = (ref: string, errorMessage: string) => {
    _setReferenceStatus(ref, { errorMessage });
  };

  const executeWithStatus = async (ref: string, execute: () => Promise<void>, onError?: (err: Error) => {}) => {
    try {
      _setReferenceStatus(ref, {
        isLoading: true,
        errorMessage: ''
      });
      await execute();
    } catch (e) {
      setErrorMessage(ref, utils.getRequestErrorMessage(e));
      if (onError) onError(e);
    } finally {
      setIsLoading(ref, false);
    }
  };

  return {
    isLoading,
    setIsLoading,
    errorMessage,
    setErrorMessage,
    executeWithStatus
  };
};

export default useLoadingStatusMap;
